import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/services', label: 'Services' },
    { path: '/pipeless-filtration', label: 'Pipeless Filtration' },
    { path: '/blog', label: 'Blog' },
    { path: '/why-choose-us', label: 'Why Choose Aquarist' },
    { path: '/contact', label: 'Contact Us' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="main-header"
      className="fixed top-0 left-0 right-0 bg-white shadow-md"
      style={{ zIndex: 9999, height: 'auto' }}
    >
      {/* Desktop Header - 100px */}
      <div className="hidden lg:block h-[100px]">
        <div className="max-w-[1400px] mx-auto px-6 h-full">
          <div className="flex justify-between items-center h-full">
            <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
              <img
                src="/assets/logo.png"
                alt="Aquarist Pools Logo"
                className="h-16 w-auto group-hover:scale-105 transition-transform duration-300"
              />
              <div>
                <h1 className="text-lg font-bold text-[#003366]">Aquarist Pools & Fitness</h1>
                <p className="text-xs text-[#00AEEF] font-semibold">Complete Pool & Spa Solutions</p>
              </div>
            </Link>

            <nav className="flex items-center justify-center flex-1 mx-8">
              <div className="flex items-center gap-1 bg-[#003366] rounded-full px-4 py-3 shadow-lg">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`
                      px-4 py-2 text-white font-medium text-sm whitespace-nowrap rounded-full
                      transition-all duration-300 relative
                      ${isActive(link.path)
                        ? 'bg-[#00AEEF] font-bold shadow-lg'
                        : 'hover:bg-[#00AEEF]/20 hover:text-[#00AEEF]'
                      }
                    `}
                  >
                    {link.label}
                    {isActive(link.path) && (
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-white rounded-full"></div>
                    )}
                  </Link>
                ))}
              </div>
            </nav>

            <div className="flex items-center gap-3 flex-shrink-0">
              <Button
                onClick={() => window.location.href = 'tel:+917757999805'}
                variant="outline"
                className="border-2 border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white transition-all duration-300"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
              <Button
                onClick={() => window.location.href = '/booking'}
                className="bg-[#00AEEF] hover:bg-[#0098d4] text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Get Quote
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Header - 70px */}
      <div className="lg:hidden h-[70px]">
        <div className="px-3 h-full flex flex-col justify-center">
          {/* Top row: Logo + Name + Get Quote + Hamburger */}
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <img
                src="/assets/logo.png"
                alt="Aquarist Pools Logo"
                className="h-9 w-auto flex-shrink-0"
              />
              <h1
                data-testid="mobile-header-logo"
                className="text-sm font-bold text-[#003366] leading-tight whitespace-nowrap"
              >
                Aquarist Pools & Fitness
              </h1>
            </Link>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                data-testid="mobile-get-quote-btn"
                onClick={() => window.location.href = '/booking'}
                className="bg-[#00AEEF] hover:bg-[#0098d4] text-white font-semibold text-xs px-3 py-1.5 h-auto shadow-md"
              >
                Get Quote
              </Button>
              <button
                data-testid="mobile-menu-toggle"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 rounded-md text-[#003366] hover:bg-gray-100 transition-colors"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
          {/* Tagline row: full width single line */}
          <p
            data-testid="mobile-header-tagline"
            className="text-[11px] text-[#00AEEF] font-semibold tracking-wide text-center w-full leading-none"
          >
            Complete Pool & Spa Solutions
          </p>
        </div>
      </div>

      {/* Mobile Navigation Dropdown */}
      {isMenuOpen && (
        <div
          data-testid="mobile-nav-menu"
          className="lg:hidden absolute left-0 right-0 bg-white shadow-2xl border-t"
          style={{ top: '70px', zIndex: 9999 }}
        >
          <nav className="flex flex-col p-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`
                  px-6 py-4 font-medium transition-all duration-300 rounded-lg mb-2
                  ${isActive(link.path)
                    ? 'bg-[#003366] text-white font-bold'
                    : 'text-[#003366] hover:bg-[#00AEEF]/10 hover:text-[#00AEEF]'
                  }
                `}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="flex flex-col gap-3 p-4 border-t">
            <Button
              onClick={() => {
                window.location.href = 'tel:+917757999805';
                setIsMenuOpen(false);
              }}
              variant="outline"
              className="w-full border-2 border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white"
            >
              <Phone className="w-4 h-4 mr-2" />
              Call Now
            </Button>
            <Button
              onClick={() => {
                window.location.href = '/booking';
                setIsMenuOpen(false);
              }}
              className="w-full bg-[#00AEEF] hover:bg-[#0098d4] text-white font-semibold"
            >
              Get Quote
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
